/**
 * Đọc hai ảnh chụp của một dòng nhật ký thành những dòng "trường: cũ → mới" (#19111).
 *
 * <p>Máy chủ trả `before_snapshot` / `after_snapshot` nguyên văn JSON — cố ý, vì mỗi loại sự kiện
 * có hình dạng riêng và tầng đọc không nên biết trước tất cả. Nhưng một ô JSON thô trên màn hình
 * thì QA vẫn phải tự dò, nên phần dịch sang tiếng người nằm ở đây, tách khỏi React để kiểm được
 * bằng test thường.
 *
 * <p><b>Chỉ ID, không tên.</b> Ticket đã chốt không chép tên bác sĩ / bệnh nhân vào nhật ký, nên
 * màn hình cũng không đi tra bù: nó rút gọn UUID cho dễ đọc và giữ nguyên bản đầy đủ trong
 * `title`. Một màn hình tự nối thêm tên sẽ dựng lại đúng thứ nhật ký cố ý không lưu.
 *
 * <p><b>Nhưng ID bấm được (#19683).</b> Một LINK không chép gì vào nhật ký: người đọc tự mở hồ sơ
 * hiện tại, qua đúng kiểm quyền của màn đó. Bệnh nhân có trang riêng; bác sĩ / phòng / khoa chỉ có
 * trang danh sách, nên link mở danh sách và tô sáng dòng đó (`?highlight=`).
 */

/** Nhãn tiếng Việt cho những khoá đã biết. Khoá lạ hiện nguyên tên — thà thô còn hơn đoán sai. */
export const AUDIT_FIELD_LABELS: Record<string, string> = {
  patientId: "Bệnh nhân",
  departmentId: "Khoa",
  doctorProfileId: "Bác sĩ",
  roomId: "Phòng",
  assignState: "Trạng thái phân công",
  status: "Trạng thái",
  startAt: "Giờ hẹn",
  durationMinutes: "Thời lượng (phút)",
};

export interface AuditFieldRow {
  key: string;
  label: string;
  from: string | null;
  to: string | null;
  changed: boolean;
}

/** JSON của một ảnh chụp, hoặc null khi không có / không đọc được. */
function parse(json: string | null | undefined): Record<string, unknown> | null {
  if (!json) return null;
  try {
    const v: unknown = JSON.parse(json);
    return v && typeof v === "object" && !Array.isArray(v)
      ? (v as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** Giá trị để so sánh: chuỗi hoá ổn định, `null`/`undefined` về cùng một thứ. */
function norm(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  return typeof v === "string" ? v : JSON.stringify(v);
}

/**
 * Các dòng của một bảng "trước / sau". Trả `[]` khi không có ảnh chụp nào đọc được — chỗ gọi hiện
 * JSON thô, vì một dòng kiểm toán có dữ liệu mà màn hình không hiểu còn tệ hơn một dòng trống.
 *
 * <p>Giữ CẢ những trường không đổi: chúng là ngữ cảnh (bệnh nhân nào, khoa nào) và là lý do mỗi
 * dòng tự đủ nghĩa. Trường đổi được đánh dấu để mắt nhảy đúng chỗ.
 */
export function auditFieldRows(
  before: string | null | undefined,
  after: string | null | undefined,
): AuditFieldRow[] {
  const b = parse(before);
  const a = parse(after);
  if (!b && !a) return [];
  const keys = [...new Set([...Object.keys(b ?? {}), ...Object.keys(a ?? {})])];
  return keys.map((key) => {
    const from = norm(b?.[key]);
    const to = norm(a?.[key]);
    return {
      key,
      label: AUDIT_FIELD_LABELS[key] ?? key,
      from,
      to,
      // Một bên vắng mặt (chỉ có ảnh SAU) thì không gọi là "đổi": nó là dữ liệu ban đầu.
      changed: b !== null && a !== null && from !== to,
    };
  });
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** UUID rút còn 8 ký tự đầu — đủ để đối chiếu bằng mắt, bản đầy đủ nằm ở `title`. */
export function shortAuditValue(v: string | null): string {
  if (v === null) return "—";
  return UUID_RE.test(v) ? v.slice(0, 8) : v;
}

/** Nơi mở một giá trị ID, hoặc null khi trường không phải ID biết trỏ đi đâu (#19683). */
const AUDIT_ID_HREF: Partial<Record<string, (id: string) => string>> = {
  patientId: (id) => `/patients/${id}`,
  doctorProfileId: (id) => `/settings/doctors?highlight=${id}`,
  roomId: (id) => `/settings/rooms?highlight=${id}`,
  departmentId: (id) => `/settings/departments?highlight=${id}`,
};

export function auditValueHref(key: string, v: string | null): string | null {
  const href = AUDIT_ID_HREF[key];
  return href && v !== null && UUID_RE.test(v) ? href(v) : null;
}
